export const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://airacer.com';
export const buildAbsoluteUrl = (path = '/') => new URL(path, `${SITE_URL}/`).href;

export const normalizeSchemaDate = (value) => {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(trimmed)) {
    return trimmed;
  }

  let match = trimmed.match(/^(\d{4})[-/](\d{2})[-/](\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    return `${year}-${month}-${day}T${hour}:${minute}:${second}+00:00`;
  }

  match = trimmed.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return `${year}-${month}-${day}T00:00:00+00:00`;
  }

  return trimmed;
};

const LOGO_URL = buildAbsoluteUrl('/imgs/svg/home/logo.svg');

export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'TravelAgency',
  name: 'Airacer Inc.',
  description:
    'Airacer, the leading private jet charter broker, specializes in serving routes across Europe, Asia, the Middle East, and North America.',
  url: SITE_URL,
  logo: LOGO_URL,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-347-383-2631',
    email: 'charter@airacer.com',
    contactType: 'Customer Service',
    availableLanguage: ['English', 'Mandarin'],
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '560 Lexington Ave',
    addressLocality: 'New York',
    addressRegion: 'NY',
    postalCode: '10022',
    addressCountry: 'US',
  },
  openingHours: 'Mo-Su 00:00-23:59',
  currenciesAccepted: 'USD, BTC',
  paymentAccepted: 'Credit Card, Wire Transfer',
  priceRange: '$$',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.6',
    reviewCount: '20',
  },
  areaServed: 'Worldwide',
});

export const buildWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Airacer',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/charterQuote?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const buildBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

/**
 * Shorthand for simple pages: auto-prepends Home breadcrumb.
 * @param {string} name - Page display name (e.g. 'Affiliate Agreement')
 * @param {string} path - URL path without origin (e.g. '/affiliateAgreement')
 * @param {Array} [extra] - Additional trailing breadcrumb items
 */
export const buildPageBreadcrumb = (name, path, extra = []) =>
  buildBreadcrumbSchema([
    { name: 'Home', url: SITE_URL },
    { name, url: `${SITE_URL}${path}` },
    ...extra,
  ]);

export const buildArticleSchema = ({
  title,
  description,
  image,
  author,
  datePublished,
  dateModified,
  url,
}) => {
  const normalizedPublishedDate = normalizeSchemaDate(datePublished);
  const normalizedModifiedDate = normalizeSchemaDate(dateModified);

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: title,
    description,
    image,
    author: {
      '@type': 'Person',
      name: author || 'Airacer',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Airacer',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    datePublished: normalizedPublishedDate,
    ...(normalizedModifiedDate ? { dateModified: normalizedModifiedDate } : {}),
  };
};

export const buildProductSchema = ({
  name,
  description,
  image,
  price,
  currency = 'USD',
  availability,
  url,
}) => {
  const validPrice = typeof price === 'number' && isFinite(price) ? price : undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image,
    url,
    ...(validPrice !== undefined && {
      offers: {
        '@type': 'Offer',
        price: validPrice,
        priceCurrency: currency,
        availability: availability || 'https://schema.org/InStock',
      },
    }),
  };
};

export const buildServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Private Jet Charter',
  description: 'Book private jet charter flights worldwide with competitive pricing.',
  provider: {
    '@type': 'Organization',
    name: 'Airacer Inc.',
    url: SITE_URL,
  },
  areaServed: 'Worldwide',
  serviceType: 'Air Charter Broker',
});

export const buildFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});
